// ===========================================
// Waitlist Entries Section Component
// ===========================================
// Displays and manages waitlist entries for a student

import { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Stack,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  School as ProgramLevelIcon,
  Class as LessonIcon,
} from '@mui/icons-material';
import { usePermissions } from '../../../hooks/usePermissions';
import { useStudentWaitlist, useAddWaitlistEntry, useRemoveWaitlistEntry } from '../../../hooks/useStudentWaitlist';
import { useLessons } from '../../../hooks/useLessons';
import { useProgramLevels } from '../../../hooks/useAdmin';

interface WaitlistEntriesSectionProps {
  studentId: string;
  isAdmin: boolean;
}

export default function WaitlistEntriesSection({
  studentId,
  isAdmin,
}: WaitlistEntriesSectionProps) {
  // Roles slice 1a: removing an entry needs people_students.write (was .delete).
  const { can } = usePermissions();
  const canRemoveEntry = can('people_students', 'write');
  // Fetch data
  const { data: entries = [], isLoading: entriesLoading } = useStudentWaitlist(studentId);
  const { data: lessons = [] } = useLessons();
  const { data: programLevels = [] } = useProgramLevels();

  // Add entry mutation
  const addWaitlistEntryMutation = useAddWaitlistEntry(studentId);

  // Remove entry mutation
  const removeWaitlistEntryMutation = useRemoveWaitlistEntry(studentId);

  // Modal state
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [entryType, setEntryType] = useState<'lesson' | 'programLevel'>('lesson');
  const [selectedLessonId, setSelectedLessonId] = useState('');
  const [selectedProgramLevelId, setSelectedProgramLevelId] = useState('');
  const [notes, setNotes] = useState('');

  // Remove confirmation state
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [entryToRemove, setEntryToRemove] = useState<string | null>(null);

  const handleAddEntry = () => {
    const lessonId = entryType === 'lesson' ? selectedLessonId : undefined;
    const programLevelId = entryType === 'programLevel' ? selectedProgramLevelId : undefined;

    if (!lessonId && !programLevelId) {
      return;
    }

    addWaitlistEntryMutation.mutate(
      {
        lessonId,
        programLevelId,
        notes: notes || undefined,
      },
      {
        onSuccess: () => {
          setAddModalOpen(false);
          setEntryType('lesson');
          setSelectedLessonId('');
          setSelectedProgramLevelId('');
          setNotes('');
        },
      }
    );
  };

  const handleRemoveEntry = () => {
    if (entryToRemove) {
      removeWaitlistEntryMutation.mutate(entryToRemove, {
        onSuccess: () => {
          setRemoveConfirmOpen(false);
          setEntryToRemove(null);
        },
      });
    }
  };

  const isAddingEntry = addWaitlistEntryMutation.isPending;
  const isRemovingEntry = removeWaitlistEntryMutation.isPending;

  // Find lesson/program level details
  const getLessonLabel = (lessonId: string | null) => {
    if (!lessonId) return null;
    const lesson = lessons.find((l) => l.id === lessonId);
    if (!lesson) return 'Unknown Lesson';
    return lesson.dayOfWeek != null
      ? `${lesson.name} (${['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][lesson.dayOfWeek]} ${lesson.startTime})`
      : `${lesson.name} (Schedule TBD)`;
  };

  const getProgramLevelLabel = (programLevelId: string | null) => {
    if (!programLevelId) return null;
    const level = programLevels.find((p) => p.id === programLevelId);
    if (!level) return 'Unknown Program Level';
    return level.name;
  };

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Waitlist Entries</Typography>
        {isAdmin && (
          <Button
            variant="contained"
            size="small"
            onClick={() => setAddModalOpen(true)}
            disabled={entriesLoading}
          >
            Add Entry
          </Button>
        )}
      </Box>
      <Divider sx={{ mb: 2 }} />

      {entriesLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress />
        </Box>
      ) : entries.length === 0 ? (
        <Alert severity="info">No waitlist entries yet.</Alert>
      ) : (
        <List>
          {entries.map((entry) => (
            <ListItem key={entry.id} divider>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {entry.lessonId ? (
                      <>
                        <LessonIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                        <span>{getLessonLabel(entry.lessonId)}</span>
                      </>
                    ) : (
                      <>
                        <ProgramLevelIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                        <span>{getProgramLevelLabel(entry.programLevelId)}</span>
                      </>
                    )}
                  </Box>
                }
                secondary={
                  <Box sx={{ mt: 1 }}>
                    {entry.notes && (
                      <Typography variant="caption" color="text.secondary">
                        Notes: {entry.notes}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Added: {new Date(entry.createdAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                }
              />
              {isAdmin && canRemoveEntry && (
                <ListItemSecondaryAction>
                  <Tooltip title="Remove Entry">
                    <IconButton
                      edge="end"
                      color="error"
                      onClick={() => {
                        setEntryToRemove(entry.id);
                        setRemoveConfirmOpen(true);
                      }}
                      disabled={isRemovingEntry}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              )}
            </ListItem>
          ))}
        </List>
      )}

      {/* Add Entry Dialog */}
      <Dialog open={addModalOpen} onClose={() => setAddModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Waitlist Entry</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={3}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Entry Type</FormLabel>
              <RadioGroup
                row
                value={entryType}
                onChange={(e) => {
                  setEntryType(e.target.value as 'lesson' | 'programLevel');
                  setSelectedLessonId('');
                  setSelectedProgramLevelId('');
                }}
              >
                <FormControlLabel value="lesson" control={<Radio />} label="Specific Lesson" />
                <FormControlLabel value="programLevel" control={<Radio />} label="Program Level" />
              </RadioGroup>
            </FormControl>

            {entryType === 'lesson' ? (
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 1 }}>Select Lesson</FormLabel>
                <select
                  value={selectedLessonId}
                  onChange={(e) => setSelectedLessonId(e.target.value)}
                  style={{
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    fontFamily: 'inherit',
                  }}
                >
                  <option value="">-- Choose a lesson --</option>
                  {lessons
                    .filter((l) => l.isActive)
                    .map((lesson) => (
                      <option key={lesson.id} value={lesson.id}>
                        {lesson.name} ({lesson.dayOfWeek != null ? `${['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][lesson.dayOfWeek]} ${lesson.startTime}` : 'TBD'})
                      </option>
                    ))}
                </select>
              </FormControl>
            ) : (
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 1 }}>Select Program Level</FormLabel>
                <select
                  value={selectedProgramLevelId}
                  onChange={(e) => setSelectedProgramLevelId(e.target.value)}
                  style={{
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    fontFamily: 'inherit',
                  }}
                >
                  <option value="">-- Choose a program level --</option>
                  {programLevels
                    .filter((p) => p.isActive)
                    .map((level) => (
                      <option key={level.id} value={level.id}>
                        {level.name}
                      </option>
                    ))}
                </select>
              </FormControl>
            )}

            <TextField
              label="Notes (optional)"
              multiline
              rows={3}
              maxRows={5}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about this waitlist entry..."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddModalOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddEntry}
            variant="contained"
            disabled={
              isAddingEntry ||
              (entryType === 'lesson' ? !selectedLessonId : !selectedProgramLevelId)
            }
          >
            {isAddingEntry ? <CircularProgress size={24} /> : 'Add Entry'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Confirmation Dialog */}
      <Dialog open={removeConfirmOpen} onClose={() => setRemoveConfirmOpen(false)}>
        <DialogTitle>Remove Waitlist Entry</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to remove this waitlist entry?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleRemoveEntry}
            color="error"
            variant="contained"
            disabled={isRemovingEntry}
          >
            {isRemovingEntry ? <CircularProgress size={24} /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
